import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core'
import { FormControl, FormGroup } from '@angular/forms'
import { tap } from 'rxjs/operators'
import * as lodash from 'lodash'
import { availableFieldFonts, RoleColor } from '../fields/constants'
import { Destroyable, untilDestroy } from '@tokeet-frontend/tv3-platform'
import { DragDropManagerService, TemplateTypes } from '@tokeet-frontend/signature'
import { DataTokenDef, SignatureDataDictHelperService } from '@tv3/services/signature-data-dict-helper.service'

enum TemplateTokenType {
  ContentBlocks = 'content_blocks',
  DataDictionary = 'data_dictionary',
  CustomCodes = 'custom_codes',
}

enum DataDictType {
  Rental = 'rental',
  Inquiry = 'inquiry',
  Guest = 'guest',
}

@Component({
  selector: 'app-template-fields-selector',
  templateUrl: './template-fields-selector.component.html',
  styleUrls: ['./template-fields-selector.component.scss'],
})
export class TemplateFieldsSelectorComponent extends Destroyable implements OnInit, OnChanges, AfterViewChecked {
  @Input() templateType: TemplateTypes
  @Input() roleColors: RoleColor[]
  @Input() form: FormGroup
  @Input() selectedField: HTMLElement
  @Input() isOnetime: boolean

  @Output() role = new EventEmitter<string>()
  @Output() selectField = new EventEmitter<HTMLElement>()

  @ViewChild('signatureDraggableTokenList') signatureDraggableTokenList: ElementRef
  @ViewChild('rentalDraggableTokenList') rentalDraggableTokenList: ElementRef
  @ViewChild('inquiryDraggableTokenList') inquiryDraggableTokenList: ElementRef
  @ViewChild('guestDraggableTokenList') guestDraggableTokenList: ElementRef

  @HostBinding('style.--active-block-color')
  get activeBlockColor() {
    return lodash.find(this.roleColors, ({ role }) => role === this.currentSignerCtrl.value)?.color
  }

  contentBlocksPanelTitle = $localize`Content Blocks`
  dataDictionaryPanelTitle = $localize`Data Dictionary`
  customCodesPanelTitle = $localize`Custom Codes`
  userNameTitle = $localize`User Name`
  inquiriesTitle = $localize`Inquiries`
  rentalsTitle = $localize`Rentals`
  guestsTitle = $localize`Guests`

  currentActiveTokenType = TemplateTokenType.ContentBlocks
  tokenTypes = TemplateTokenType
  dataDictTypes = DataDictType
  templateTypes = TemplateTypes

  fonts = availableFieldFonts
  fontSizes = lodash.range(10, 21)

  currentSignerCtrl = new FormControl()
  dataTokensByType: { [type: string]: DataTokenDef[] } = {}

  constructor(public dataDicHelper: SignatureDataDictHelperService, public signFieldManager: DragDropManagerService) {
    super()
    // put it in constructor
    this.currentSignerCtrl.valueChanges
      .pipe(
        tap((role) => this.role.emit(role)),
        untilDestroy(this)
      )
      .subscribe()
  }

  ngOnInit(): void {
    this.init()
  }

  ngOnChanges(changes: SimpleChanges) {}

  ngAfterViewChecked() {
    this.signFieldManager.addDraggableContainer(this.signatureDraggableTokenList)
    this.signFieldManager.addDraggableContainer(this.rentalDraggableTokenList)
    this.signFieldManager.addDraggableContainer(this.inquiryDraggableTokenList)
    this.signFieldManager.addDraggableContainer(this.guestDraggableTokenList)
  }

  getDataDicFieldLabel(dataDicHelper: SignatureDataDictHelperService, templateType: TemplateTypes, item: DataTokenDef) {
    if (templateType === TemplateTypes.Form && dataDicHelper.isGuestField(item.key)) {
      return item.label
    } else {
      return item.key
    }
  }

  onFieldClick(elm: HTMLElement) {
    this.selectField.emit(elm)
  }

  init() {
    this.currentSignerCtrl.patchValue(lodash.head(this.roleColors).role)

    this.dataTokensByType = lodash.reduce(
      this.dataDicHelper.getResolvers([this.templateType], undefined, undefined, undefined, undefined),
      (res, tokensByKey, type) => {
        res[type] = lodash.sortBy(lodash.values(tokensByKey), (item: DataTokenDef) => item.label)
        return res
      },
      {}
    )
  }
}
